import {
  IUserRepository,
  CreateUserCommand,
  UpdateUserCommand,
  AssignRoleCommand,
  LinkBusinessCommand
} from '../../domain/interfaces/IUserRepository';
import { User } from '../../domain/entities/User';
import { UserRole } from '../../domain/value-objects/UserRole';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { UserListItemDto, UserDetailDto } from '../dto/UserDtos';

/**
 * UserManagementService - Application service for user management operations
 *
 * Orchestrates user CRUD, role assignment, and business linking
 * Most operations are admin-only
 */
export class UserManagementService {
  constructor(private readonly userRepository: IUserRepository) {}

  /**
   * Get all users with pagination (admin only)
   * Returns UserListItemDto with business information
   */
  async getAllUsers(params?: PaginationParams, userRole?: UserRole): Promise<PaginatedResult<UserListItemDto>> {
    // Only admins can view all users
    if (userRole && userRole !== 'Admin') {
      throw new Error('Only admins can view all users');
    }

    return this.userRepository.getAll(params);
  }

  /**
   * Get a single user by ID
   * Returns UserDetailDto with complete information
   * - Admin: Can view any user
   * - BusinessOwner: Can only view themselves
   */
  async getUserById(id: string, userRole: UserRole, currentUserId: string): Promise<UserDetailDto> {
    // Admin can view any user
    if (userRole === 'Admin') {
      return this.userRepository.getById(id);
    }

    // BusinessOwner can only view themselves
    if (userRole === 'BusinessOwner') {
      if (id !== currentUserId) {
        throw new Error('Access denied: You can only view your own profile');
      }
      return this.userRepository.getById(id);
    }

    throw new Error(`Unknown user role: ${userRole}`);
  }

  /**
   * Create a new user (admin only)
   */
  async createUser(command: CreateUserCommand, userRole: UserRole): Promise<User> {
    // Only admins can create users
    if (userRole !== 'Admin') {
      throw new Error('Only admins can create users');
    }

    // Validate required fields
    if (!command.userName || !command.email || !command.password || !command.role) {
      throw new Error('Missing required fields: userName, email, password, role');
    }

    // Validate email format
    if (!this.isValidEmail(command.email)) {
      throw new Error('Invalid email format');
    }

    // Validate BusinessOwner has businessId
    if (command.role === 'BusinessOwner' && !command.businessId) {
      throw new Error('BusinessOwner users must have a businessId');
    }

    return this.userRepository.create(command);
  }

  /**
   * Update a user
   * - Admin: Can update any user
   * - Customer: Can only update themselves (limited fields)
   */
  async updateUser(
    id: string,
    command: UpdateUserCommand,
    userRole: UserRole,
    currentUserId: string
  ): Promise<User> {
    // Admin can update any user
    if (userRole === 'Admin') {
      // Validate email if provided
      if (command.email && !this.isValidEmail(command.email)) {
        throw new Error('Invalid email format');
      }

      return this.userRepository.update(id, command);
    }

    // BusinessOwner can only update themselves
    if (userRole === 'BusinessOwner') {
      if (id !== currentUserId) {
        throw new Error('Access denied: You can only update your own profile');
      }

      // BusinessOwners have limited update permissions
      const allowedFields: (keyof UpdateUserCommand)[] = [
        'firstName',
        'lastName',
        'phoneNumber',
        'preferredLanguage',
        'timeZone'
      ];

      // Filter command to only allowed fields
      const filteredCommand: UpdateUserCommand = {};
      for (const key of allowedFields) {
        if (command[key] !== undefined) {
          (filteredCommand as any)[key] = command[key];
        }
      }

      // Validate email if provided (though customers shouldn't be able to change it)
      if (command.email && !this.isValidEmail(command.email)) {
        throw new Error('Invalid email format');
      }

      return this.userRepository.update(id, filteredCommand);
    }

    throw new Error(`Unknown user role: ${userRole}`);
  }

  /**
   * Delete a user (admin only)
   */
  async deleteUser(id: string, userRole: UserRole): Promise<void> {
    // Only admins can delete users
    if (userRole !== 'Admin') {
      throw new Error('Only admins can delete users');
    }

    await this.userRepository.delete(id);
  }

  /**
   * Assign a role to a user (admin only)
   */
  async assignUserRole(userId: string, role: UserRole, adminRole: UserRole): Promise<void> {
    // Only admins can assign roles
    if (adminRole !== 'Admin') {
      throw new Error('Only admins can assign roles');
    }

    const command: AssignRoleCommand = {
      userId,
      role
    };

    await this.userRepository.assignRole(command);
  }

  /**
   * Link a customer to a business (admin only)
   */
  async linkCustomerToBusiness(
    userId: string,
    businessId: string,
    adminRole: UserRole
  ): Promise<void> {
    // Only admins can link customers to businesses
    if (adminRole !== 'Admin') {
      throw new Error('Only admins can link customers to businesses');
    }

    const command: LinkBusinessCommand = {
      userId,
      businessId
    };

    await this.userRepository.linkBusinessToCustomer(command);
  }

  /**
   * Validate that a customer has a business link
   */
  async validateCustomerBusinessLink(
    userId: string,
    userRole: UserRole
  ): Promise<{ valid: boolean; businessId: string | null }> {
    const user = await this.userRepository.getById(userId);

    if (userRole !== 'BusinessOwner') {
      return { valid: true, businessId: null };
    }

    return {
      valid: !!user.businessId,
      businessId: user.businessId
    };
  }

  /**
   * Assign a business to a user (admin only - Sprint 010)
   */
  async assignBusiness(userId: string, businessId: string, adminRole: UserRole): Promise<void> {
    // Only admins can assign businesses
    if (adminRole !== 'Admin') {
      throw new Error('Only admins can assign businesses to users');
    }

    await this.userRepository.assignBusiness(userId, businessId);
  }

  /**
   * Remove a business from a user (admin only - Sprint 010)
   */
  async removeBusiness(userId: string, adminRole: UserRole): Promise<void> {
    // Only admins can remove businesses
    if (adminRole !== 'Admin') {
      throw new Error('Only admins can remove businesses from users');
    }

    await this.userRepository.removeBusiness(userId);
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}
